import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { ArrowRight, X } from './Icons';
import { useTranslation } from '../contexts/TranslationContext';
import { LANDING_SECTION_TITLE_CLASS_NAME, landingSectionClassName } from './landingSectionClassName';
import './LandingPressSection.css';

const PRESS_MENTIONS = [
  {
    id: 'frenchTech',
    name: 'French Tech Alpes',
    href: 'https://www.ftalps.com/en/company/palen-by-kairisio/',
    logo: '/assets/press/french-tech-alpes.svg',
    width: 110,
    height: 90,
  },
  {
    id: 'adrInfo',
    name: 'ADR INFO SERVICE',
    href: 'https://www.conseiller-securite-adr.fr/innovation-adr',
    logo: '/assets/press/adr-info-service.png',
    width: 378,
    height: 134,
  },
  {
    id: 'gmjPhoenix',
    name: 'GMJ Phoenix',
    href: 'https://gmjphoenix.com/logiciels-transport-de-marchandises-dangereuses/palen-plateforme-intelligence-artificielle-adr/',
    logo: '/assets/press/gmj-phoenix-vector.svg',
    width: 262,
    height: 101,
  },
  {
    id: 'kairisio',
    name: 'Kairisio',
    href: 'https://kairisio.com/#projets',
    logo: '/assets/press/kairisio-wordmark.png',
    width: 640,
    height: 213,
  },
];

export function LandingPressSection({ isVisible }: { isVisible: boolean }) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(PRESS_MENTIONS[0].id);
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);
  const [isKeyboardMode, setIsKeyboardMode] = useState(false);
  const [quotePosition, setQuotePosition] = useState({ left: 0, top: 0, tail: 0 });
  const viewportRef = useRef<HTMLDivElement>(null);
  const quoteRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const isRestoringFocusRef = useRef(false);
  const quoteFocusTargetRef = useRef<'a' | 'button' | null>(null);

  const positionQuote = useCallback((button: HTMLButtonElement) => {
    const carousel = viewportRef.current?.parentElement;
    if (!carousel || !quoteRef.current) return;
    const bounds = carousel.getBoundingClientRect();
    const width = quoteRef.current.offsetWidth;
    if (!bounds.width || !width) return;
    const logo = button.getBoundingClientRect();
    const center = Math.max(0, Math.min(bounds.width, logo.left + logo.width / 2 - bounds.left));
    const left = Math.max(0, Math.min(bounds.width - width, center - width / 2));
    const top = logo.bottom - bounds.top;
    const tail = center - left;
    setQuotePosition((current) => current.left === left && current.top === top && current.tail === tail ? current : { left, top, tail });
  }, []);

  useEffect(() => {
    if (!isQuoteOpen) return;
    if (quoteFocusTargetRef.current) {
      const control = quoteRef.current?.querySelector<HTMLElement>(
        `[data-active="true"] ${quoteFocusTargetRef.current}`,
      );
      quoteFocusTargetRef.current = null;
      control?.focus({ preventScroll: true });
    }
    const update = () => { if (anchorRef.current) positionQuote(anchorRef.current); };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isQuoteOpen, isKeyboardMode, selectedId, positionQuote]);

  function revealQuote(id: string, button: HTMLButtonElement) {
    if (isRestoringFocusRef.current) return;
    const focusedControl = document.activeElement;
    if (selectedId !== id && quoteRef.current?.contains(focusedControl)) {
      if (focusedControl?.tagName === 'A') quoteFocusTargetRef.current = 'a';
      if (focusedControl?.tagName === 'BUTTON') quoteFocusTargetRef.current = 'button';
      // Hold focus outside the outgoing article before it becomes inert.
      quoteRef.current.focus({ preventScroll: true });
    }
    anchorRef.current = button;
    positionQuote(button);
    openerRef.current = viewportRef.current?.querySelector<HTMLButtonElement>(
      `.press-logo-group:not([aria-hidden]) [data-press-source="${id}"]`,
    ) ?? button;
    setSelectedId(id);
    setIsQuoteOpen(true);
  }

  function closeQuote(restoreFocus = false) {
    quoteFocusTargetRef.current = null;
    setIsQuoteOpen(false);
    if (restoreFocus) {
      isRestoringFocusRef.current = true;
      openerRef.current?.focus({ preventScroll: true });
      // Ignore hover caused by the logo layout changing during focus restoration.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => { isRestoringFocusRef.current = false; });
      });
    }
  }

  return (
    <section
      id="press"
      data-quote-open={isQuoteOpen}
      aria-labelledby="press-title"
      className={landingSectionClassName({
        fillAvailable: false,
        className: 'press-section px-4 py-14 sm:px-6 sm:py-24',
      })}
    >
      <div className="press-quote-backdrop" data-open={isQuoteOpen} aria-hidden="true" />
      <div
        id="press-content"
        data-landing-scroll-target
        data-animate
        className={`relative z-10 mx-auto w-full max-w-6xl transition-all duration-1000 motion-reduce:transform-none motion-reduce:transition-none ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}
      >
        <div className="mx-auto mb-8 max-w-3xl text-center sm:mb-10">
          <h2 id="press-title" className={LANDING_SECTION_TITLE_CLASS_NAME}>
            {t('press.title')}
          </h2>
          <p className="text-base leading-7 text-[rgb(var(--theme-muted))] sm:text-lg">
            {t('press.description')}
          </p>
        </div>

        <div
          className="press-carousel"
          data-testid="press-carousel"
          data-keyboard={isKeyboardMode}
          data-selected={isQuoteOpen}
          onPointerLeave={(event) => {
            if (event.pointerType === 'mouse' && !event.currentTarget.contains(document.activeElement)) {
              closeQuote();
            }
          }}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              closeQuote();
              setIsKeyboardMode(false);
              if (viewportRef.current) viewportRef.current.scrollLeft = 0;
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              closeQuote(Boolean(quoteRef.current?.contains(document.activeElement)));
            }
          }}
        >
          <div ref={viewportRef} className="press-logo-marquee">
            <div className="press-logo-track">
              {[false, true].map((isDuplicate) => (
                <div key={String(isDuplicate)} className="press-logo-group" aria-hidden={isDuplicate || undefined}>
                  {PRESS_MENTIONS.map(({ id, name, logo, width, height }) => (
                    <button
                      key={id}
                      type="button"
                      className="press-logo-button brand-focus-ring"
                      tabIndex={isDuplicate ? -1 : 0}
                      aria-label={`${t('press.showExcerpt')} — ${name}`}
                      aria-controls="press-quote"
                      aria-expanded={isQuoteOpen && selectedId === id}
                      data-press-source={id}
                      onMouseEnter={(event) => revealQuote(id, event.currentTarget)}
                      onFocus={(event) => {
                        if (event.currentTarget.matches(':focus-visible')) setIsKeyboardMode(true);
                        revealQuote(id, event.currentTarget);
                      }}
                      onClick={(event) => revealQuote(id, event.currentTarget)}
                      onMouseDown={(event) => { if (isDuplicate) event.preventDefault(); }}
                    >
                      <img
                        src={logo}
                        alt={name}
                        width={width}
                        height={height}
                        loading="lazy"
                        decoding="async"
                        className="press-logo-image"
                      />
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div
            id="press-quote"
            ref={quoteRef}
            tabIndex={-1}
            className="press-quote-popover"
            style={{
              '--press-panel-left': `${quotePosition.left}px`,
              '--press-panel-top': `${quotePosition.top}px`,
              '--press-tail-x': `${quotePosition.tail}px`,
            } as CSSProperties}
            data-open={isQuoteOpen}
            aria-hidden={!isQuoteOpen || undefined}
            {...(!isQuoteOpen ? { inert: '' } : {})}
          >
            <svg className="press-quote-tail" viewBox="0 0 44 24" aria-hidden="true">
              <path d="M0 24C12 24 18 11 22 0C26 11 32 24 44 24Z" />
            </svg>
            {PRESS_MENTIONS.map((mention) => {
              const isSelected = selectedId === mention.id;
              return (
                <article
                  key={mention.id}
                  data-active={isSelected}
                  aria-hidden={!isSelected || undefined}
                  {...(!isSelected ? { inert: '' } : {})}
                  data-testid={isQuoteOpen && isSelected ? 'press-quote' : undefined}
                  aria-labelledby={`press-quote-${mention.id}-title`}
                  className="press-quote-card"
                >
                  <button
                    type="button"
                    aria-label={t('press.closeQuote')}
                    onClick={() => closeQuote(true)}
                    className="press-quote-close brand-focus-ring"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <div className="press-quote-source" data-press-source={mention.id}>
                    <div className="press-quote-source-logo">
                      <img
                        src={mention.logo}
                        alt={mention.name}
                        width={mention.width}
                        height={mention.height}
                        decoding="async"
                      />
                    </div>
                  </div>
                  <div className="press-quote-copy">
                    <span className="press-quote-mark" aria-hidden="true">“</span>
                    <h3 id={`press-quote-${mention.id}-title`} className="press-quote-headline">
                      {t(`press.${mention.id}.title`)}
                    </h3>
                    <blockquote cite={mention.href} className="press-quote-text">
                      <p>« {t(`press.${mention.id}.quote`)} »</p>
                    </blockquote>
                    <a
                      href={mention.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${t('press.readArticle')} — ${mention.name} (${t('press.newTab')})`}
                      className="press-quote-link brand-focus-ring"
                    >
                      {t('press.readArticle')}
                      <span className="press-quote-link-arrow" aria-hidden="true">
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </a>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
